/*
 * myPokemonImportRunner.ts — the Poké Genie import as a page-independent job.
 *
 * The import used to live inside the import sheet's submit handler, which
 * made two device-reported problems (2026-08-20): no visible progress ("I
 * didn't know if it was working"), and the sheet had to stay open for the
 * whole run. This module owns the run instead: any page can start it, the
 * global progress bar (MyPokemonImportBar, mounted in App) shows x/y rows
 * wherever the trainer navigates, and closing the sheet changes nothing.
 *
 * Scope honesty: "background" means WITHIN the app. When iOS suspends or
 * jettisons the webview (home button, app switch), all JS stops with it —
 * nothing any web code can prevent. The mitigations are real though: the
 * import completes in seconds now, and re-importing the same CSV is
 * idempotent (exact-identity rows merge), so an interrupted run is safely
 * re-run.
 */
import { useSyncExternalStore } from "react";

import { parsePokeGenieCsv } from "./pokeGenieImport";
import {
  importMyPokemonRecords,
  listMyPokemon,
  type ImportSummary,
} from "./myPokemonStore";
import {
  crossWriteMyPokemonToDex,
  type DexCrossWriteResult,
} from "./myPokemonDexCrossWrite";

export type MyPokemonImportPhase =
  | "idle"
  | "importing"
  | "done"
  | "error";

export interface MyPokemonImportState {
  phase: MyPokemonImportPhase;
  /** Rows merged so far / total rows, while importing. */
  done: number;
  total: number;
  summary: ImportSummary | null;
  /** Rows the CSV parser could not read at all. */
  rejected: number;
  /** Rows whose Costume cell named nothing that species wears (template files). */
  costumesUnrecognised: number;
  /** Dex cells the import newly ticked (see myPokemonDexCrossWrite). */
  dex: DexCrossWriteResult | null;
  error: string | null;
}

const IDLE: MyPokemonImportState = {
  phase: "idle",
  done: 0,
  total: 0,
  summary: null,
  rejected: 0,
  costumesUnrecognised: 0,
  dex: null,
  error: null,
};

let state: MyPokemonImportState = IDLE;
let running = false;
let hideTimer: number | null = null;
const listeners = new Set<() => void>();

function setState(next: MyPokemonImportState): void {
  state = next;
  for (const l of listeners) l();
}

/** Clear a finished (done/error) banner. No-op mid-run. */
export function dismissMyPokemonImport(): void {
  if (state.phase === "importing") return;
  if (hideTimer != null) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
  setState(IDLE);
}

function autoHide(ms: number): void {
  if (hideTimer != null) clearTimeout(hideTimer);
  hideTimer = setTimeout(() => {
    hideTimer = null;
    dismissMyPokemonImport();
  }, ms) as unknown as number;
}

/**
 * Start the import. Returns false when one is already running (the CSV picker
 * can't reasonably queue two) or the file is not a Poké Genie export.
 */
export async function startPokeGenieImport(
  csvText: string,
): Promise<boolean> {
  if (running) return false;
  const parsed = parsePokeGenieCsv(csvText);
  if (parsed.unrecognised) {
    setState({
      ...IDLE,
      phase: "error",
      error:
        parsed.headerSample && parsed.headerSample.length > 0
          ? `Not a Poké Genie export or Nexus Dex template: no “Pokemon Number” and “CP” columns. This file's columns start: ${parsed.headerSample.join(", ")}. In Poké Genie, open the Pokémon list, Export, CSV; or download the template from the import sheet.`
          : "Not a Poké Genie export or Nexus Dex template: no “Pokemon Number” and “CP” columns, or the file was empty. In Poké Genie, open the Pokémon list, Export, CSV; or download the template from the import sheet.",
    });
    autoHide(8000);
    return false;
  }
  running = true;
  setState({
    ...IDLE,
    phase: "importing",
    total: parsed.observations.length,
  });
  try {
    const summary = await importMyPokemonRecords(
      parsed.observations,
      (done, total) => {
        if (state.phase === "importing") setState({ ...state, done, total });
      },
    );
    if (summary.storageFailed) {
      setState({
        ...IDLE,
        phase: "error",
        error:
          "The rows read fine but this device's storage refused the write. Free some space (or delete old records) and try again.",
      });
      autoHide(10000);
      return true;
    }
    // Tick the Pokédex from everything now owned (additive + roster-gated;
    // covers pre-existing records too, which is idempotent by design).
    const dex = crossWriteMyPokemonToDex(listMyPokemon());
    setState({
      phase: "done",
      done: parsed.observations.length,
      total: parsed.observations.length,
      summary,
      rejected: parsed.rejected,
      costumesUnrecognised: parsed.costumesUnrecognised,
      dex,
      error: null,
    });
    autoHide(10000);
    return true;
  } catch {
    setState({ ...IDLE, phase: "error", error: "Couldn't read that file." });
    autoHide(8000);
    return true;
  } finally {
    running = false;
  }
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
function getSnapshot(): MyPokemonImportState {
  return state;
}
function getServerSnapshot(): MyPokemonImportState {
  return IDLE;
}

/** Live import state, for the global progress bar and the import sheet. */
export function useMyPokemonImport(): MyPokemonImportState {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/** Test seam. */
export function __resetMyPokemonImportForTest(): void {
  running = false;
  if (hideTimer != null) clearTimeout(hideTimer);
  hideTimer = null;
  state = IDLE;
}
